import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownTrayIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  DescriptionList,
  EmptyState,
  ErrorState,
  JobStatusBadge,
  Modal,
  PageHeader,
  SelectField,
  SkeletonRows,
  useToast,
  type Column,
} from '@csa/ui';
import { isTerminal, type JobStatus } from '@csa/shared';
import { api, ApiError } from '../../lib/api.js';
import { useTimeZone } from '../../lib/TimeZoneProvider.js';
import { ResultPanel, type ResultSet } from '../sql/ResultPanel.js';

/**
 * Jobs and downloads.
 *
 * The list is the answer to "what did I run, and where is the file", which
 * is the question this page exists for. It is also the only place a large
 * export can be collected, because a batch result never comes back in a
 * response -- it is written to S3 and handed over as a presigned URL minted
 * at the moment of the click.
 *
 * Whose jobs are shown is decided by the server, not by this page. The
 * response carries `scope`, and the page reports it; a user without
 * `job.view.all` who somehow reached the "everyone" filter still receives
 * only their own rows, and the heading will say so.
 */

interface JobSummary {
  readonly id: string;
  readonly kind: 'sql' | 'api';
  readonly status: JobStatus;
  readonly mode: string;
  readonly targetId: string;
  readonly targetKey: string;
  readonly environment: string;
  readonly submittedByUpn: string;
  readonly statementClass: string | null;
  readonly credentialKind: 'read' | 'write';
  readonly payloadSummary: string;
  readonly justification: string;
  readonly rowCount: number | null;
  readonly byteCount: number | null;
  readonly httpStatus: number | null;
  readonly truncated: boolean;
  readonly hasResult: boolean;
  readonly hasInlineResult: boolean;
  readonly error: { code: string; message: string; retryable: boolean } | null;
  readonly attempt: number;
  readonly approvalRequestId: string | null;
  readonly createdAt: string;
  readonly queuedAt: string | null;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
  readonly durationMs: number | null;
}

interface InlineResult {
  readonly results: readonly ResultSet[];
}

interface JobPage {
  readonly jobs: readonly JobSummary[];
  readonly scope: 'own' | 'all';
  readonly nextCursor: string | null;
}

interface ApprovalTrail {
  readonly id: string;
  readonly status: string;
  readonly requiredApprovals: number;
  readonly decisions: readonly {
    readonly approverUpn: string;
    readonly decision: string;
    readonly comment: string;
    readonly decidedAt: string;
  }[];
}

/**
 * The filters offered, as sets rather than individual statuses.
 *
 * "Is it still going" and "did it work" are the two questions people
 * actually arrive with. A nine-item status dropdown makes them compose the
 * answer themselves, and `pending-approval` in particular is easy to miss
 * when it is one option among nine.
 */
const LENSES = {
  all: { label: 'All jobs', statuses: undefined },
  active: {
    label: 'In progress',
    statuses: ['pending-approval', 'approved', 'queued', 'running'] as JobStatus[],
  },
  waiting: { label: 'Waiting for approval', statuses: ['pending-approval'] as JobStatus[] },
  succeeded: { label: 'Succeeded', statuses: ['succeeded'] as JobStatus[] },
  problems: {
    label: 'Failed, rejected or expired',
    statuses: ['failed', 'rejected', 'expired'] as JobStatus[],
  },
} as const;

type Lens = keyof typeof LENSES;

const PAGE_SIZE = 50;

export function JobsPage({ canViewAll }: { canViewAll: boolean }): ReactElement {
  const tz = useTimeZone();
  const [lens, setLens] = useState<Lens>('all');
  const [mine, setMine] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);

  const statuses = LENSES[lens].statuses;

  const jobs = useInfiniteQuery({
    queryKey: ['jobs', lens, mine],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), mine: String(mine) });
      if (statuses) params.set('status', statuses.join(','));
      if (pageParam) params.set('cursor', pageParam);
      return api.get<JobPage>(`/api/jobs?${params.toString()}`, { signal });
    },
    getNextPageParam: (last) => last.nextCursor,
    /**
     * A job list is the one screen where staleness is actively misleading --
     * somebody watching for an approval or an export needs it to move on its
     * own. Polling rather than streaming because this is a list of many jobs;
     * the per-job SSE stream is for the console that submitted one.
     */
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });

  const rows = useMemo(() => jobs.data?.pages.flatMap((p) => p.jobs) ?? [], [jobs.data]);
  const scope = jobs.data?.pages[0]?.scope ?? 'own';

  const columns = useMemo<Column<JobSummary>[]>(
    () => [
      {
        key: 'status',
        header: 'Status',
        render: (job) => (
          <span className="flex items-center gap-2">
            <JobStatusBadge status={job.status} />
            {job.truncated ? <Badge tone="warning">truncated</Badge> : null}
          </span>
        ),
      },
      {
        key: 'what',
        header: 'Request',
        render: (job) => (
          <span className="flex flex-col gap-0.5">
            <span className="flex items-center gap-2 text-sm text-body">
              <Badge tone="neutral">{job.kind}</Badge>
              <code className="font-mono text-xs">{job.targetKey}</code>
            </span>
            {/*
              The summary is a redacted description built server-side, never
              the statement itself. A job list that printed SQL would put
              parameter values from somebody else's extract on this screen.
            */}
            <span className="truncate text-xs text-muted" title={job.payloadSummary}>
              {job.payloadSummary}
            </span>
          </span>
        ),
      },
      ...(scope === 'all'
        ? [
            {
              key: 'who',
              header: 'Submitted by',
              render: (job: JobSummary) => <span className="text-xs">{job.submittedByUpn}</span>,
            },
          ]
        : []),
      {
        key: 'rows',
        header: 'Rows',
        numeric: true,
        render: (job) => (
          <span className="text-xs tabular-nums">
            {job.rowCount === null ? '—' : job.rowCount.toLocaleString()}
          </span>
        ),
      },
      {
        key: 'took',
        header: 'Took',
        numeric: true,
        render: (job) => (
          <span className="text-xs tabular-nums">
            {job.durationMs === null ? '—' : `${job.durationMs.toLocaleString()} ms`}
          </span>
        ),
      },
      {
        key: 'created',
        header: `Submitted (${tz.timeZone})`,
        numeric: true,
        render: (job) => (
          <time
            dateTime={job.createdAt}
            className="text-xs text-muted"
            title={tz.titleFor(job.createdAt)}
          >
            {tz.formatBare(job.createdAt).value}
          </time>
        ),
      },
      {
        key: 'actions',
        header: '',
        render: (job) => (
          <Button variant="ghost" size="xs" onClick={() => setSelected(job.id)}>
            Open
          </Button>
        ),
      },
    ],
    [scope, tz],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Jobs & downloads"
        description="Everything you have submitted, and where to collect the results."
      />

      <Card className="flex flex-wrap items-end gap-3 p-3">
        <SelectField
          label="Show"
          value={lens}
          onChange={(event) => setLens(event.target.value as Lens)}
          options={Object.entries(LENSES).map(([value, l]) => ({ value, label: l.label }))}
          containerClassName="w-60"
        />

        {canViewAll ? (
          <SelectField
            label="Whose"
            value={mine ? 'mine' : 'everyone'}
            onChange={(event) => setMine(event.target.value === 'mine')}
            options={[
              { value: 'mine', label: 'Mine' },
              { value: 'everyone', label: 'Everyone' },
            ]}
            containerClassName="w-44"
            description="Viewing another person's jobs is itself audited."
          />
        ) : null}

        <span className="ml-auto text-xs text-subtle">
          {rows.length.toLocaleString()} shown
          {jobs.hasNextPage ? ' · more available' : ''}
          {scope === 'own' ? ' · your jobs only' : ' · all users'}
        </span>
      </Card>

      {jobs.isError ? (
        <ErrorState
          title="Could not load jobs"
          message={jobs.error instanceof ApiError ? jobs.error.message : 'Unexpected error.'}
          action={<Button onClick={() => void jobs.refetch()}>Retry</Button>}
        />
      ) : jobs.isPending ? (
        <SkeletonRows rows={8} />
      ) : (
        <Card className="flex flex-col gap-3 p-3">
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(job) => job.id}
            onRowClick={(job) => setSelected(job.id)}
            caption="Submitted jobs"
            empty={
              <EmptyState
                title="Nothing here yet"
                description={
                  lens === 'all'
                    ? 'Jobs appear here as soon as you run something in the SQL or API console.'
                    : 'No jobs match this filter.'
                }
              />
            }
          />

          {jobs.hasNextPage ? (
            <Button
              variant="secondary"
              size="sm"
              className="self-center"
              loading={jobs.isFetchingNextPage}
              onClick={() => void jobs.fetchNextPage()}
            >
              Load more
            </Button>
          ) : null}
        </Card>
      )}

      {selected ? <JobDetail jobId={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}

function JobDetail({ jobId, onClose }: { jobId: string; onClose: () => void }): ReactElement {
  const tz = useTimeZone();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const job = useQuery({
    queryKey: ['job', jobId],
    queryFn: ({ signal }) => api.get<JobSummary>(`/api/jobs/${jobId}`, { signal }),
    // Stops polling the moment the job can no longer change.
    refetchInterval: (query) =>
      query.state.data && isTerminal(query.state.data.status) ? false : 3_000,
  });

  /**
   * Fetched only when the job actually went through approval. A 404 here is
   * the normal answer for a job that did not, and turning that into an error
   * banner would make the common case look broken.
   */
  const approval = useQuery({
    queryKey: ['job', jobId, 'approval'],
    enabled: Boolean(job.data?.approvalRequestId),
    queryFn: ({ signal }) => api.get<ApprovalTrail>(`/api/jobs/${jobId}/approval`, { signal }),
  });

  const cancel = useMutation({
    mutationFn: () => api.post<{ status: string }>(`/api/jobs/${jobId}/cancel`),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Cancellation requested' });
      void queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (err) =>
      toast({
        tone: 'danger',
        title: 'Could not cancel',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
      }),
  });

  /**
   * The presigned URL is fetched on click and navigated to immediately.
   *
   * It is never held in state and never becomes an `href`. A presigned URL
   * is a bearer credential for the object: anything that puts it in the DOM
   * puts it in a screenshot, the browser history, and the reach of every
   * installed extension.
   */
  /**
   * Loaded only when the job actually has one, so opening a job that
   * produced a file -- or whose inline result has aged out -- does not
   * issue a request that can only 404.
   */
  const inline = useQuery({
    queryKey: ['job-result', jobId],
    queryFn: () => api.get<InlineResult>(`/api/jobs/${jobId}/result`),
    enabled: job.data?.hasInlineResult === true,
  });

  const download = useCallback(async (): Promise<void> => {
    setDownloading(true);
    setError(null);
    try {
      const { url } = await api.post<{ url: string }>(`/api/jobs/${jobId}/download`);
      window.location.assign(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not get a download link');
    } finally {
      setDownloading(false);
    }
  }, [jobId]);

  const data = job.data;
  const cancellable = data ? !isTerminal(data.status) : false;

  return (
    <Modal open onClose={onClose} title="Job" size="lg">
      {job.isError ? (
        <ErrorState
          title="Could not load this job"
          message={job.error instanceof ApiError ? job.error.message : 'Unexpected error.'}
          action={<Button onClick={() => void job.refetch()}>Retry</Button>}
        />
      ) : !data ? (
        <SkeletonRows rows={6} />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <JobStatusBadge status={data.status} />
            <Badge tone="neutral">{data.kind}</Badge>
            <Badge tone={data.credentialKind === 'write' ? 'warning' : 'neutral'}>
              {data.credentialKind} account
            </Badge>
            {data.attempt > 1 ? <Badge tone="warning">attempt {data.attempt}</Badge> : null}
          </div>

          {data.error ? (
            /**
             * A job that is still queued carries the error from its last
             * attempt, so titling this "the job failed" unconditionally
             * contradicted the status badge beside it and told people a
             * job was over while it was waiting to be retried.
             */
            <Alert
              tone={isTerminal(data.status) ? 'danger' : 'warning'}
              title={
                isTerminal(data.status)
                  ? 'The job failed'
                  : `Attempt ${data.attempt} failed — it will be retried`
              }
            >
              {data.error.message}
              <span className="ml-2 font-mono text-xs opacity-70">{data.error.code}</span>
              {data.error.retryable && isTerminal(data.status) ? (
                <p className="mt-1 text-xs">
                  This failure was transient. Resubmitting is likely to succeed.
                </p>
              ) : null}
            </Alert>
          ) : null}

          {data.hasInlineResult ? (
            <div className="flex flex-col gap-2">
              {inline.isPending ? (
                <SkeletonRows rows={4} />
              ) : inline.isError ? (
                <Alert tone="warning">
                  {inline.error instanceof ApiError
                    ? inline.error.message
                    : 'Could not load the result.'}
                </Alert>
              ) : inline.data ? (
                <>
                  <ResultPanel results={inline.data.results} />
                  <p className="text-xs text-subtle">
                    Kept for 24 hours after the job finishes
                    {inline.data.results.length > 1
                      ? ` · ${inline.data.results.length} of the script's statements ran`
                      : ''}
                  </p>
                </>
              ) : null}
            </div>
          ) : null}

          {data.truncated ? (
            <Alert tone="warning" title="Results were truncated">
              The target&apos;s interactive row cap was reached. Run this as a batch export to get
              the complete result.
            </Alert>
          ) : null}

          <DescriptionList
            items={[
              {
                label: 'Target',
                value: <code className="font-mono text-xs">{data.targetKey}</code>,
              },
              { label: 'Environment', value: data.environment },
              { label: 'Mode', value: data.mode },
              ...(data.statementClass ? [{ label: 'Statement', value: data.statementClass }] : []),
              ...(data.httpStatus !== null
                ? [{ label: 'HTTP status', value: String(data.httpStatus) }]
                : []),
              { label: 'Submitted by', value: data.submittedByUpn },
              { label: 'Justification', value: data.justification },
              {
                label: 'Summary',
                value: <span className="break-words">{data.payloadSummary}</span>,
              },
              {
                label: 'Rows',
                value: data.rowCount === null ? '—' : data.rowCount.toLocaleString(),
              },
              {
                label: 'Bytes',
                value: data.byteCount === null ? '—' : data.byteCount.toLocaleString(),
              },
              {
                label: 'Submitted',
                value: <span title={tz.titleFor(data.createdAt)}>{tz.format(data.createdAt)}</span>,
              },
              {
                label: 'Started',
                value: data.startedAt ? (
                  <span title={tz.titleFor(data.startedAt)}>{tz.format(data.startedAt)}</span>
                ) : (
                  '—'
                ),
              },
              {
                label: 'Finished',
                value: data.finishedAt ? (
                  <span title={tz.titleFor(data.finishedAt)}>{tz.format(data.finishedAt)}</span>
                ) : (
                  '—'
                ),
              },
              {
                label: 'Job id',
                value: <code className="font-mono text-xs">{data.id}</code>,
              },
            ]}
          />

          {approval.data ? (
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-body">Approval</h3>
              <p className="text-xs text-muted">
                {approval.data.decisions.length} of {approval.data.requiredApprovals} recorded ·{' '}
                {approval.data.status}
              </p>
              <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
                {approval.data.decisions.map((decision) => (
                  <li key={`${decision.approverUpn}:${decision.decidedAt}`} className="p-3">
                    <p className="flex flex-wrap items-center gap-2 text-sm text-body">
                      <Badge tone={decision.decision === 'granted' ? 'success' : 'danger'}>
                        {decision.decision}
                      </Badge>
                      {decision.approverUpn}
                      <span className="text-xs text-subtle" title={tz.titleFor(decision.decidedAt)}>
                        {tz.format(decision.decidedAt)}
                      </span>
                    </p>
                    <p className="mt-1 text-xs text-muted">{decision.comment}</p>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {error ? (
            <Alert tone="danger" title="Download failed">
              {error}
            </Alert>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            {data.hasResult ? (
              <Button
                variant="secondary"
                leadingIcon={<ArrowDownTrayIcon className="size-4" aria-hidden />}
                onClick={() => void download()}
                disabled={downloading}
              >
                {downloading ? 'Preparing…' : 'Download result'}
              </Button>
            ) : null}

            {cancellable ? (
              <Button
                variant="ghost"
                leadingIcon={<NoSymbolIcon className="size-4" aria-hidden />}
                loading={cancel.isPending}
                onClick={() => cancel.mutate()}
              >
                Cancel
              </Button>
            ) : null}

            <Button variant="ghost" className="ml-auto" onClick={onClose}>
              Close
            </Button>
          </div>

          {data.hasResult ? (
            <p className="text-xs text-subtle">
              The download link is valid for a few minutes, is tied to this job, and its issue is
              recorded in the audit trail.
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
